import {z} from 'zod';
import {portSchema} from './port';

/**
 * Node state reflects the lifecycle of a node.
 */
export const nodeStateSchema = z.enum([
  'idle',
  'awaiting_config',
  'running',
  'completed',
  'error',
  'disabled',
]);

export type NodeState = z.infer<typeof nodeStateSchema>;

export const nodeTypeSchema = z.enum([
  'trigger', // Workflow entry points
  'action', // Perform operations
  'transformer', // Transform data
  'control_flow', // Flow control (conditionals, loops)
  'ai',
]);

export type NodeType = z.infer<typeof nodeTypeSchema>;

/**
 * Supported AI model providers for agent nodes.
 */
export const modelProviderSchema = z.enum([
  'claude',
  'openai',
  'gemini',
  'unknown',
]);

export type ModelProvider = z.infer<typeof modelProviderSchema>;

export const nodeMetadataSchema = z.object({
  sourceType: z.string().optional(),
  sourceVersion: z.union([z.string(), z.number()]).optional(),
  iconColor: z.string().optional(),
  sourceId: z.string().optional(),
  sourceData: z.record(z.unknown()).optional(),
  // Agent / chain LLM node metadata
  modelProvider: modelProviderSchema.optional(),
  modelName: z.string().optional(),
  toolCount: z.number().optional(),
  systemMessage: z.string().optional(),
});

export type NodeMetadata = z.infer<typeof nodeMetadataSchema>;

export const nodeSchema = z.object({
  id: z.string().startsWith('node_'),
  type: nodeTypeSchema,
  capability: z.string().min(1),
  label: z.string().min(1),
  description: z.string().optional(),
  state: nodeStateSchema.default('idle'),
  icon: z.string().optional(),
  actionVerb: z.string().optional(),
  subtitle: z.string().optional(),
  config: z.record(z.unknown()).default({}),
  inputs: z.array(portSchema).default([]),
  outputs: z.array(portSchema).default([]),
  metadata: nodeMetadataSchema,
});

export type Node = z.infer<typeof nodeSchema>;

export interface NodeExecutionBase {
  executionTimeMs?: number;
  outputData?: Record<string, unknown>[];
  inputData?: Record<string, unknown>[];
  error?: string;
}

/**
 * Per-iteration execution data for a node that ran more than once (e.g. a node
 * inside a loop). Each entry is one terminal run of the node, in execution
 * order. The node's top-level fields reflect the last iteration.
 */
export interface NodeIterationState extends NodeExecutionBase {
  state: 'completed' | 'error';
  // Loop pass (0-based) this run belongs to. Absent when it can't be resolved;
  // consumers then fall back to array order.
  iterationIndex?: number;
}

/**
 * Node-level execution state from polling n8n's execution API.
 * Used to drive real-time visual feedback on the workflow canvas.
 */
export interface NodeExecutionState extends NodeExecutionBase {
  nodeId: string;
  nodeName: string;
  state: 'idle' | 'running' | 'completed' | 'error';
  // Loop nodes only: the number of completed iterations (loop-body runs).
  iteration?: {
    current: number;
  };
  // Per-iteration breakdown for loops where a node ran more than once.
  iterations?: NodeIterationState[];
  // The node n8n executed last; the run Output view renders this node.
  isFinalOutput?: boolean;
}
